import type { QueryDataSetterUpdater } from "./useQueryData";

/** Ключ элемента списка в query cache. */
export type CachedListItemKey = number | string;

/**
 * Берёт `Code` как ключ элемента списка. \
 * Удобный default для большинства stock/workshop сущностей.
 * @param item элемент с полем `Code`
 */
export function byCode<T extends { Code: number }>(item: T): number {
    return item.Code;
}

/**
 * Заменяет элемент списка в TanStack Query cache по ключу.
 * @param setData setter списка из {@link useQueryData}
 * @param item актуальный элемент
 * @param getKey ключ сравнения элементов
 */
export function replaceCachedListItem<T>(
    setData: (updater: QueryDataSetterUpdater<T[]>) => void,
    item: T,
    getKey: (item: T) => CachedListItemKey,
): void {
    const key = getKey(item);
    setData((prevData) => prevData?.map((current) => (getKey(current) === key ? item : current)));
}

/**
 * Вставляет элемент в список или заменяет существующий с тем же ключом.
 * @param setData setter списка из {@link useQueryData}
 * @param item элемент для upsert
 * @param getKey ключ сравнения элементов
 */
export function upsertCachedListItem<T>(
    setData: (updater: QueryDataSetterUpdater<T[]>) => void,
    item: T,
    getKey: (item: T) => CachedListItemKey,
): void {
    const key = getKey(item);
    setData((prevData) => {
        if (prevData == null) {
            return [item];
        }

        const index = prevData.findIndex((current) => getKey(current) === key);
        if (index < 0) {
            return [...prevData, item];
        }

        return prevData.map((current, currentIndex) => (currentIndex === index ? item : current));
    });
}

/**
 * Удаляет элемент списка из query cache по ключу.
 * @param setData setter списка из {@link useQueryData}
 * @param key ключ удаляемого элемента
 * @param getKey ключ сравнения элементов
 */
export function removeCachedListItem<T>(
    setData: (updater: QueryDataSetterUpdater<T[]>) => void,
    key: CachedListItemKey,
    getKey: (item: T) => CachedListItemKey,
): void {
    setData((prevData) => prevData?.filter((current) => getKey(current) !== key));
}

/**
 * Патчит поля существующего элемента списка без полной перезагрузки.
 * @param setData setter списка из {@link useQueryData}
 * @param key ключ элемента
 * @param patch частичное обновление полей
 * @param getKey ключ сравнения элементов
 */
export function patchCachedListItem<T>(
    setData: (updater: QueryDataSetterUpdater<T[]>) => void,
    key: CachedListItemKey,
    patch: Partial<T>,
    getKey: (item: T) => CachedListItemKey,
): void {
    setData((prevData) => prevData?.map((current) => (getKey(current) === key ? { ...current, ...patch } : current)));
}

/**
 * Перезагружает один элемент и патчит его в cache списка. \
 * Если `loadItem` вернул `null` и задан `removeKeyIfMissing` — элемент удаляется из списка
 * (например, резерв полностью отпущен).
 * @param options загрузка и патч элемента
 * @returns загруженный элемент или `null`
 */
export async function reloadAndReplaceCachedListItem<T>(options: {
    setData: (updater: QueryDataSetterUpdater<T[]>) => void;
    loadItem: () => Promise<T | null | undefined>;
    getKey: (item: T) => CachedListItemKey;
    removeKeyIfMissing?: CachedListItemKey;
}): Promise<T | null> {
    const item = await options.loadItem();

    if (item == null) {
        if (options.removeKeyIfMissing != null) {
            removeCachedListItem(options.setData, options.removeKeyIfMissing, options.getKey);
        }
        return null;
    }

    const loadedItem = item;
    const key = options.getKey(loadedItem);
    options.setData((prevData) => prevData?.map((current) => (options.getKey(current) === key ? loadedItem : current)));
    return loadedItem;
}
